import React from 'react';
import ReactDOM from 'react-dom';
import {
    mimeTypes, 
    moduleNames, 
    modules, 
    normalizedModuleNames, 
    searchTerms, 
    marketDynamicsTerms, 
    constructionTypeMap,
    agentProductionTerms,
} from '../../constants';
import { csvExportDateFormat, shortDateFormatWithSlashes } from '../../agent-production/helpers/dataFormatters';
import { terms as t, terms } from '../constants';
import { marketShareTerms as mst } from '../../market-share/constants';
import { reverseMap } from '../../common/helpers/utilities';
import { fieldsToExclude, getAgentDetailsFileName } from '../constants/pdf';
import SummaryTable from '../components/common/pdf/SummaryTable';

export const renderComponentToHtml = (Component, props = {}) => {
    const tempContainer = document.createElement('div');
    ReactDOM.render(<Component {...props} />, tempContainer);
    const html = tempContainer.innerHTML;
    ReactDOM.unmountComponentAtNode(tempContainer);
    return html;
};

export const getFieldValues = (loadedSearchCriteria, fieldName) => {
    const field = loadedSearchCriteria?.realEstateDatasourceIdsWithFilters[0].searchFields.find(
        (f) => f.fieldName === fieldName
    );
    if (!field) return searchTerms.all;
    if (fieldName === searchTerms.newConstruction) return reverseMap(constructionTypeMap)[field.fieldValues[0]];
    if (field.fieldValueDescriptions) return field.fieldValueDescriptions.join(', ');
    if (field.fieldMinValue !== undefined && field.fieldMaxValue !== undefined) {
        return `${field.fieldMinValue}-${field.fieldMaxValue}`;
    }
    if (field.fieldMinValue !== undefined) return `${field.fieldMinValue}+`;
    if (field.fieldMaxValue !== undefined) return `0-${field.fieldMaxValue}`;
    return searchTerms.all;
};

export const getSelectedAreaInfo = (loadedSearchCriteria, module, areaTypeTitle) => {

    if (module === modules.agentProduction.agentProduction) {
        return { selectedAreaType: searchTerms.lowerAllMls, selectedArea: '' };
    }

    if(module === modules.marketShare.coverage){
        return {
            selectedAreaType: areaTypeTitle + ':',
            selectedArea: searchTerms.all
        };
    }

    if (loadedSearchCriteria?.realEstateDatasourceIdsWithFilters[0].searchAllMLS) {
        return { selectedAreaType: searchTerms.lowerAllMls, selectedArea: '' };
    }
    const areaField = loadedSearchCriteria?.realEstateDatasourceIdsWithFilters[0].searchFields.find(
        (f) => f.fieldTypeName
    );
    if (areaField) {
        return {
            selectedAreaType: areaField.fieldTypeName + ':',
            selectedArea: areaField.fieldValues.join(', '),
        };
    }
    return { selectedAreaType: '', selectedArea: '' };
};

export const truncateLine = (text, maxLength) => {
    const ellipsis = '...';
    if (text.length <= maxLength) return text;
    let truncated = text.substr(0, maxLength);
    let lastSpaceIndex = truncated.lastIndexOf(' ');
    if (lastSpaceIndex === -1) {
        return truncated.replace(/,$/, '') + ellipsis;
    }
    let cleanedText = truncated.substr(0, lastSpaceIndex).replace(/,$/, '');
    return cleanedText + ellipsis;
};

export const downloadPDF = async (streamResponse, module, listingViewId, exportDetails, config) => {
    const pdfBlob = await streamResponse.blob().then((blob) => new Blob([blob], { type: mimeTypes.pdf }));
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = pdfUrl;
    downloadLink.download = config.getPdfFileName(listingViewId, exportDetails);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(pdfUrl);
};

export const get6MonthsWeeklyRange = (searchResults) => {
    const lastTimePeriod = searchResults[searchResults.length - 1].timePeriodStart;
    const firstTimePeriod = searchResults[0].timePeriodEnd;
    return `${shortDateFormatWithSlashes(lastTimePeriod)}-${shortDateFormatWithSlashes(firstTimePeriod)}`;
};

export const getTimeFrame = (searchResults, timeFrameTitle, selectedBeginValue, selectedEndValue) => {
    const is6MonthsWeekly = timeFrameTitle?.mainTitle === marketDynamicsTerms.months6Weekly;
    const timeFrame = is6MonthsWeekly
        ? get6MonthsWeeklyRange(searchResults)
        : `${shortDateFormatWithSlashes(selectedBeginValue)}-${shortDateFormatWithSlashes(selectedEndValue)}`;

    return timeFrame;
};

export const getPdfClassname = (isOverview, index, isSelector = false) => {
    if (isSelector) return isOverview ? `.chart-capture-area-${index}` : '.chart-capture-area';
    return isOverview ? `chart-capture-area-${index}` : 'chart-capture-area';
};

export const getMarketShareFileName = (listingViewId, module) => {
            return  `${normalizedModuleNames[moduleNames.marketShare]}'s 
            ${normalizedModuleNames[module]} ${listingViewId} 
            ${terms.report} - ${csvExportDateFormat(new Date())}.pdf`.replace(/\s+/g, ' ');
};

const getAgentMovementFileName = () => {
    return `${normalizedModuleNames[modules.proficiencyMetrics.agentMovement]} 
    ${terms.report} - ${terms.pdf} - 
    ${csvExportDateFormat()}.pdf`.replace(/\s+/g, ' ');
};

const getDefaultDetailFields = ({ selectedMls, timeFrame, loadedSearchCriteria, listingTypeId, isComparisonSetView, listingStatus }) => {
    return [
        { label: searchTerms.mls, value: selectedMls.shortDescription },
        { label: searchTerms.timeFrame, value: timeFrame },
        ...(listingTypeId ? [{
            label: mst.type,
            value: isComparisonSetView ? mst.byComparisonSet : mst[listingTypeId]
        }] : []),
        ...(listingStatus ? [{ label: mst.status, value: listingStatus }] : []),
        { label: terms.price, value: getFieldValues(loadedSearchCriteria, 'lastPrice') },
        { label: searchTerms.constructionType, value: getFieldValues(loadedSearchCriteria, 'newConstruction') },
        { label: agentProductionTerms.bedrooms, value: getFieldValues(loadedSearchCriteria, 'bedrooms') },
        { label: terms.bathrooms, value: getFieldValues(loadedSearchCriteria, 'fullBathrooms') },
        { label: searchTerms.lotSize, value: getFieldValues(loadedSearchCriteria, 'lotSizeAcres') },
        { label: terms.sqFt, value: getFieldValues(loadedSearchCriteria, 'squareFeet') },
    ];
};

const getAMDetailFields = ({ selectedMls, timeFrame }) => {
    return [
        { label: searchTerms.mls, value: selectedMls.shortDescription },
        { label: searchTerms.timeFrame, value: timeFrame }
    ];
};

const getADDetailFields = ({ selectedMls, timeFrame, loadedSearchCriteria }) => {
    return [
        { label: searchTerms.mls, value: selectedMls.shortDescription },
        { label: searchTerms.timeFrame, value: timeFrame },
        { label: agentProductionTerms.bedrooms, value: getFieldValues(loadedSearchCriteria, 'bedrooms') },
        { label: terms.bathrooms, value: getFieldValues(loadedSearchCriteria, 'fullBathrooms') },
        { label: searchTerms.constructionType, value: getFieldValues(loadedSearchCriteria, 'newConstruction') },
    ];
};

const baseAgentDetailsConfig = {
    getBrandName: (isAgentMetrics) => isAgentMetrics ? t.loneWolfAgentProduction : t.loneWolfProficiencyMetrics,
    getHeaderTitle: (_, {isAgentMetrics}) => isAgentMetrics ? agentProductionTerms.agentProduction : agentProductionTerms.agentDetails,
    hasTempBody: true,
    hasTable: false,
    getPdfFileName: (_, exportDetails) => getAgentDetailsFileName(exportDetails),
    moduleTitle: (selectedTabName) => selectedTabName,
    shouldShowOfficeListFooter: true,
    shouldShowPropertyTypes: true,
    getDetailFields: getADDetailFields,
    hasAgentDetailHeader: true,
};

const renderChartDefault = ({ chartImage }) => (
    <div className={"chart-container"}>
        <img src={chartImage} alt={agentProductionTerms.chart} />
    </div>
);

export const agentDetailReportConfig = {
    production: {
        ...baseAgentDetailsConfig,
        renderChart: ({ chartImage, productionSummary }) => (
            <div className="agent-profile-chart-container">
                <SummaryTable {...productionSummary} />
                <img src={chartImage} alt={agentProductionTerms.chart} />
            </div>
        ),
        hasTable: true,
        hasSummaryTable: true,
        hasTempBody: false,
        rowsPerTable: 20,
        hasTotalsRow: true,
        hasBoldTotalsLabel: true,
    },
    transactionCoverage: {
        ...baseAgentDetailsConfig,
                renderChart: ({ chartImage, selectedAgentProfileArea }) => (
            <div className='chart-wrapper'>
                <div className='area-header'>
                    {agentProductionTerms.areaType}: <strong>{selectedAgentProfileArea}</strong>
                </div>
                <div className='chart-container'>
                    <img
                        src={chartImage}
                        alt={agentProductionTerms.chart} />
                </div>
            </div>
        ),
        hasTable: true,
        rowsPerTable: 23,
        hasTempBody: false,
        hasTotalsRow: true,
        hasBoldTotalsLabel: true,
    },
       inventory: {
        ...baseAgentDetailsConfig,
        renderChart: () => <></>,
        hasTable: true,
        hasTempBody: false,
        rowsPerTable: 23,
    },
};

export const reportConfig = {
    [modules.marketDynamics.marketDynamics]: {
        getBrandName: () => t.loneWolfMarketDynamics,
        getHeaderTitle: (timeFrameTitle, { headerOverviewTabName }) => `${headerOverviewTabName}- ${timeFrameTitle.mainTitle}`,
        shouldShowOfficeListHeader: false,
        hasTempBody: false,
        hasTable: true,
        getPdfFileName: (listingViewId) => 
            `${normalizedModuleNames[modules.marketDynamics.marketDynamics]}' ${terms[listingViewId]} ${terms.report} - ${csvExportDateFormat(new Date())}.pdf`,
        hasTotalsRow: false,
        moduleTitle: () => normalizedModuleNames[moduleNames.marketDynamics],
        shouldShowPropertyTypes: true,
        getDetailFields: getDefaultDetailFields,
        shouldShowOfficeListFooter: false,
        rowsPerTable: 25,
        hasTruncation: false,
        renderChart: ({ chartImage, isOverview }) => (
            <div className={`chart-container ${isOverview ? 'overview-report' : ''}`}>
                <img src={chartImage} alt={agentProductionTerms.chart} />
            </div>
        ),
    },
    [modules.marketShare.coverage]: {
        getBrandName: () => t.loneWolfMarketShare,
        getHeaderTitle: (_, { areaTypeTitle }) => `(${terms.getBySearchTypeTitle(areaTypeTitle)})`,
        shouldShowOfficeListHeader: true,
        hasTempBody: false,
        hasTable: true,
        getPdfFileName: (listingViewId) => getMarketShareFileName(listingViewId, modules.marketShare.coverage),
        hasTotalsRow: true,
        moduleTitle: () => normalizedModuleNames[modules.marketShare.coverage],
        shouldShowPropertyTypes: true,
        getDetailFields: getDefaultDetailFields,
        shouldShowOfficeListFooter: false,
        rowsPerTable: 25,
        renderChart: renderChartDefault,
        hasTruncation: false,
    },
    [modules.marketShare.totals]: {
        getBrandName: () => t.loneWolfMarketShare,
        getHeaderTitle: (_, { isComparisonSetView, selectedComparisonSet, listingTypeId }) => 
            `(${isComparisonSetView ? terms.getBySearchTypeTitle(selectedComparisonSet?.name) : mst[listingTypeId]})`,
        shouldShowOfficeListHeader: false,
        hasTempBody: false,
        hasTable: true,
        getPdfFileName: (listingViewId) => getMarketShareFileName(listingViewId, modules.marketShare.totals),
        hasTotalsRow: true,
        moduleTitle: () => normalizedModuleNames[modules.marketShare.totals],
        shouldShowPropertyTypes: true,
        getDetailFields: getDefaultDetailFields,
        shouldShowOfficeListFooter: false,
        rowsPerTable: 25,
        renderChart: renderChartDefault,
        hasTruncation: false,
    },
    [modules.proficiencyMetrics.agentMovement]: {
        getBrandName: () => t.loneWolfProficiencyMetrics,
        getHeaderTitle: () => normalizedModuleNames[modules.proficiencyMetrics.agentMovement],
        hasTempBody: false,
        hasTable: true,
        getPdfFileName: () => getAgentMovementFileName(),
        hasTotalsRow: true,
        moduleTitle: () => normalizedModuleNames[moduleNames.proficiencyMetrics],
        shouldShowOfficeListFooter: true,
        shouldShowOfficeListHeader: false,
        shouldShowPropertyTypes: false,
        getDetailFields: getAMDetailFields,
        rowsPerTable: 26,
        renderChart: renderChartDefault,
        hasTruncation: true,
    },
    [modules.proficiencyMetrics.transactions]: agentDetailReportConfig,
    [modules.proficiencyMetrics.savedAgents]: agentDetailReportConfig,
    [modules.proficiencyMetrics.agents]: agentDetailReportConfig,
    [modules.proficiencyMetrics.suggestedAgents]: agentDetailReportConfig,
    [modules.proficiencyMetrics.offices]: agentDetailReportConfig,
    [modules.agentProduction.agentProduction]: agentDetailReportConfig,
};

export const flattenTable = (tableView) => {
    if (!Array.isArray(tableView) || !tableView[0]?.children) return;
    const flatTables = [];

    tableView.forEach(table => {
        table.children.forEach(child => {
            flatTables.push({
                col: {
                    id: child.col.id,
                    label: child.col.label,
                    keyField: child.col.id
                },
                colDef: {
                    label: child.col.label,
                    cellClass: child.colDef.cellClass || '',
                    headerClass: child.colDef.headerClass || '',
                    valueFormatter: child.colDef.valueFormatter || (({ value }) => value)
                }
            });
        });
    });

    return { tables: flatTables };
};

export const transformTableView = (tableView) => {

    if (!tableView) return ({ tables: [] });

    if (Array.isArray(tableView?.tables)) {
        return tableView;
    }

    const filteredTableView = tableView.filter(colDef => !fieldsToExclude.includes(colDef.field) && !colDef.hide);

    return {
        tables: filteredTableView.map(colDef => ({
            col: {
                id: colDef.field || colDef.id,
                label: colDef.headerName || colDef.label,
                keyField: colDef.field || colDef.id
            },
            colDef: {
                label: colDef.headerName || colDef.label,
                cellClass: colDef.cellClass || '',
                headerClass: colDef.headerClass || '',
                valueFormatter: colDef.valueFormatter || (({ value }) => value)
            },
        }))
    };
};

export const normalizePhoneNumber = (phoneNumber) => {
    const nums = [];
    if (!phoneNumber?.length) return agentProductionTerms.notApplicableAbbrv;
    for (const char of phoneNumber) {
        if (!isNaN(parseInt(char))) {
            nums.push(char);
        }
    };
    return `(${nums[0]}${nums[1]}${nums[2]})-${nums[3]}${nums[4]}${nums[5]}-${nums[6]}${nums[7]}${nums[8]}${nums[9]}`;
};

export const agentDetailFullAddress = ({ officeAddress, officeCity, officeState, officeZipCode }) => [
    (officeAddress || '').trim(),
    (officeCity || '').trim(),
    (officeState || '').trim(),
    (officeZipCode || '').trim()
].filter((ad) => !!ad).join(', ');
